import React from 'react';
import { Alert, Box, Button, Link, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import generateAudioStore from '../../Mobx/GenerateAudioStore';
import { PROVIDER_INWORLD, extensionOfMime, formatError } from '../../Mobx/AudioCatalog';

const formatSize = (bytes) => (bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/** A name for the downloaded file: the text, the model and the voice */
const fileNameOf = (audio) => {
    const text = audio.text.trim().replace(/[\\/:*?"<>|\s]+/g, '-').slice(0, 40);
    return `${text || 'audio'}_${audio.model}_${audio.name || 'default-voice'}.${extensionOfMime(audio.mime)}`;
};

/** The options that Inworld got (it takes no prompt): the pace, the delivery and the niqqud */
const inworldOptionsOf = (audio) => [
    `rate: ${audio.speakingRate}`,
    audio.deliveryMode ? `delivery: ${audio.deliveryMode}` : null,
    audio.niqqud ? `niqqud: ${audio.niqqud}` : null,
].filter(Boolean).join(' · ');

const AudioGenerated = observer(() => {
    const { generatedAudios, error } = generateAudioStore;

    return (
        <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">Audio Generated</Typography>
                {generatedAudios.length > 0 && (
                    <Button size="small" onClick={generateAudioStore.clearAudios}>Clear</Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Box sx={{ whiteSpace: 'pre-wrap' }}><bdi>Error generating audio: {formatError(error)}</bdi></Box>
                </Alert>
            )}

            {generatedAudios.length === 0 && !error && (
                <Typography variant="body2" color="text.secondary">
                    Nothing yet. Choose a provider, write a text (and a prompt for Gemini), choose a voice and a model, and press Generate.
                    Every file that is created stays here (the newest first), so voices and models can be compared.
                </Typography>
            )}

            {generatedAudios.map((audio) => (
                <Box key={audio.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                        #{audio.id} &nbsp;
                        <bdi>{audio.text.length > 80 ? `${audio.text.slice(0, 80)}…` : audio.text}</bdi>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <bdi>
                            {audio.model} · {audio.name || '(default voice)'} · {audio.language}
                            {' · '}{(audio.ms / 1000).toFixed(1)} s · {formatSize(audio.bytes)} · {audio.mime}
                        </bdi>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        <bdi>{audio.provider === PROVIDER_INWORLD ? inworldOptionsOf(audio) : `prompt: ${audio.prompt}`}</bdi>
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                        <audio controls src={audio.url} autoPlay={Date.now() - audio.createdAt < 3000} style={{ flexGrow: 1, maxWidth: 520 }}>
                            Your browser does not support the audio element.
                        </audio>
                        <Link href={audio.url} download={fileNameOf(audio)} underline="hover" variant="body2">
                            Download
                        </Link>
                    </Box>
                </Box>
            ))}
        </Box>
    );
});

export default AudioGenerated;
